import {
  Box,
  Icon,
  Input,
  ScrollArea,
  Text,
} from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter, SettingsBackBar, SettingsBackBarClearance, } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupRow } from "./settings-group";
import { PermissionBadges, type Permissions } from "./permission-badge";
import { formatDate } from "#app/lib/utils/format-date";
import { CopyIconButton } from "#app/components/copy-icon-button";
import type { Omittable } from "@salestrak/ui";

type ApiKeyViewProps = Omittable<{
  // The nested object relaxes too — `Omittable` is not recursive, and the
  // create screens hand this a freshly-minted key whose `creationDate` is
  // whatever the mutation returned.
  apiKey: Omittable<{
    name: string;
    permissions: Permissions;
    creationDate?: string | null;
  }>;
  secret?: string | null;
  onDone: () => void;
}>;

export function ApiKeyView({ apiKey, secret, onDone }: ApiKeyViewProps) {



  const dateLabel = apiKey.creationDate ? formatDate(apiKey.creationDate) : null;

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="API" onClick={onDone} />
      <ScrollArea>
        <SettingsBackBarClearance />
        <PageHeader centered pinnedBack>
          <PageHeaderTitle>{apiKey.name}</PageHeaderTitle>
          {dateLabel ? (
            <PageHeaderSubtitle>Created {dateLabel}</PageHeaderSubtitle>
          ) : null}
        </PageHeader>
        <Box align="center" direction="column" paddingX="md" paddingY="md">
          <Box direction="column" fill="width" gap="2xl" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            {/* The title names the key; the card is the secret and what it
              * may do. */}
            <SettingsGroup label="Key">
              {secret ? (
                <SettingsGroupRow label="API key">
                  <Box align="center" direction="row" fill="width" gap="sm">
                    <Box fill="width" minWidth={0} shrink>
                      {/* Read-only reveal field: focusing selects the whole
                        * secret so a keyboard copy needs no drag. */}
                      <Input
                        onFocus={(e) => e.currentTarget.select()}
                        readOnly
                        value={secret}
                      />
                    </Box>
                    <CopyIconButton text={secret} what="API key" />
                  </Box>
                </SettingsGroupRow>
              ) : null}

              <SettingsGroupRow label="Permissions">
                <PermissionBadges permissions={apiKey.permissions} />
              </SettingsGroupRow>
            </SettingsGroup>

            {secret ? (
              <Box surface="sunken" padding="md" rounded="md">
                <Box align="start" direction="row" gap="sm">
                  <Icon color="muted" name="alert-triangle" size="md" />
                  <Text size="xs" variant="muted">
                    Copy this key now. You won't be able to see it again.
                  </Text>
                </Box>
              </Box>
            ) : null}

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
